import { useQuery } from '@tanstack/react-query';
import { queryConfigs } from '@/providers/tanstack/query-client';
import { featureFlagsApiService } from '@/services/feature-flags.service';

import { ActiveFlagsMap } from '@/types/entities/feature-flag';

// Shared query key — all instances resolve to the same cached request
const ACTIVE_FLAGS_QUERY_KEY = ['featureFlags', 'active'];

/**
 * Returns the boolean value of a single feature flag.
 * All instances share one HTTP request via the shared query key.
 *
 * @example
 * const isEnabled = useFeatureFlag('beta-mode');
 * <ShowIf condition={isEnabled}><BetaComponent /></ShowIf>
 */
export function useFeatureFlag(flagKey: string): boolean {
  const { data } = useQuery<ActiveFlagsMap>(
    ACTIVE_FLAGS_QUERY_KEY,
    () => featureFlagsApiService.getActive(),
    { ...queryConfigs.referenceData }
  );

  return data?.[flagKey] ?? false;
}

/**
 * Returns the full active flags map.
 * Intended for admin use (e.g. feature-flag-list-view).
 */
export function useFeatureFlags(): ActiveFlagsMap {
  const { data } = useQuery<ActiveFlagsMap>(
    ACTIVE_FLAGS_QUERY_KEY,
    () => featureFlagsApiService.getActive(),
    { ...queryConfigs.referenceData }
  );

  return data ?? {};
}
